import { CheckSquare, ClipboardPaste, Copy, Crosshair, Highlighter, History, MousePointer2, Pentagon, Ruler, Scissors, Spline, SquareDashed, RotateCcw, RotateCw, Trash2, Undo2, Wand2, Minus, Type, Move, Eraser, Stamp } from "lucide-react";

const Btn = ({ icon: Icon, label, active, disabled, onClick, testid }) => (
  <button
    type="button"
    title={label}
    aria-label={label}
    aria-pressed={active || undefined}
    disabled={disabled}
    onClick={onClick}
    data-testid={testid}
    className={`flex h-10 w-10 items-center justify-center rounded-lg transition-colors ${
      active ? "bg-[#f97316] text-[#0b172a]" : "text-slate-300 hover:bg-white/10 hover:text-white"
    } disabled:cursor-not-allowed disabled:opacity-30`}
  >
    <Icon className="h-5 w-5" />
  </button>
);

/** 64 px navy left tool rail — measurement tools, then page/edit actions.
 * Viewer mode strips it to review-only tools (select / box / rotate). */
export default function ToolRail({
  tool, setTool, hasDrawing, hasDraft, hasSelection, canPaste, snap,
  onRotate, onSelectAll, onCopy, onPaste, onUndoPoint, onDeleteSelected,
  canUndo, onUndo, viewer = false, cad = false,
}) {
  const t = (k) => () => setTool(k);
  if (viewer) {
    return (
      <div
        className="flex w-16 shrink-0 flex-col items-center gap-1 overflow-y-auto bg-[#0b172a] py-2"
        data-testid="takeoff-tool-rail-viewer"
      >
        <Btn icon={MousePointer2} label="Select / review" active={tool === "select"} disabled={!hasDrawing} onClick={t("select")} testid="tool-select" />
        <Btn icon={SquareDashed} label="Box select (review)" active={tool === "box"} disabled={!hasDrawing} onClick={t("box")} testid="tool-box" />
        <div className="my-1 h-px w-8 bg-white/15" />
        <Btn icon={RotateCcw} label="Rotate page left 90°" disabled={!hasDrawing} onClick={() => onRotate(-1)} testid="tool-rotate-left" />
        <Btn icon={RotateCw} label="Rotate page right 90°" disabled={!hasDrawing} onClick={() => onRotate(1)} testid="tool-rotate-right" />
      </div>
    );
  }
  return (
    <div
      className="grid w-24 shrink-0 grid-cols-2 content-start justify-items-center gap-1 overflow-y-auto bg-[#0b172a] px-1 py-2"
      data-testid="takeoff-tool-rail"
    >
      <Btn icon={MousePointer2} label="Select / review" active={tool === "select"} disabled={!hasDrawing} onClick={t("select")} testid="tool-select" />
      <Btn icon={SquareDashed} label="Box select (Shift adds)" active={tool === "box"} disabled={!hasDrawing} onClick={t("box")} testid="tool-box" />
      <Btn icon={Highlighter} label="Highlight" active={tool === "highlight"} disabled={!hasDrawing} onClick={t("highlight")} testid="tool-highlight" />
      <Btn icon={Scissors} label="Cut linear takeoff (C)" active={tool === "cut"} disabled={!hasDrawing} onClick={t("cut")} testid="tool-cut" />
      <Btn icon={Ruler} label="Scale — click two points of a known dimension" active={tool === "scale"} disabled={!hasDrawing} onClick={t("scale")} testid="tool-scale" />
      {cad && <Btn icon={Wand2} label="CAD pick — click a line, pipe or outline to add it as a takeoff with its exact CAD length; click a block to count every matching block on the page" active={tool === "cadpick"} disabled={!hasDrawing} onClick={t("cadpick")} testid="tool-cadpick" />}
      {cad && (
        <>
          <div className="col-span-2 my-1 h-px w-16 bg-slate-200" />
          <Btn icon={Minus} label="CAD line — two clicks (snaps to CAD points)" active={tool === "cadline"} disabled={!hasDrawing} onClick={t("cadline")} testid="tool-cadline" />
          <Btn icon={Spline} label="CAD polyline — click points, double-click to finish" active={tool === "cadpoly"} disabled={!hasDrawing} onClick={t("cadpoly")} testid="tool-cadpoly" />
          <Btn icon={Type} label="CAD text — click where the note goes" active={tool === "cadtext"} disabled={!hasDrawing} onClick={t("cadtext")} testid="tool-cadtext" />
          <Btn icon={Ruler} label="CAD dimension — click two points to label the real distance (saved to the DXF)" active={tool === "caddim"} disabled={!hasDrawing} onClick={t("caddim")} testid="tool-caddim" />
          <Btn icon={Stamp} label="Stamp symbol — place the manhole / hydrant / catch basin picked in the Layers panel (saved to the DXF)" active={tool === "cadsymbol"} disabled={!hasDrawing} onClick={t("cadsymbol")} testid="tool-cadsymbol" />
          <Btn icon={Move} label="CAD move — click an entity, then its new position" active={tool === "cadmove"} disabled={!hasDrawing} onClick={t("cadmove")} testid="tool-cadmove" />
          <Btn icon={Copy} label="CAD offset — click an entity, then the side/distance to copy it to" active={tool === "cadoffset"} disabled={!hasDrawing} onClick={t("cadoffset")} testid="tool-cadoffset" />
          <Btn icon={Eraser} label="CAD delete — click an entity to remove it" active={tool === "caddelete"} disabled={!hasDrawing} onClick={t("caddelete")} testid="tool-caddelete" />
        </>
      )}
      <Btn icon={Spline} label={snap ? "Linear — angle snap ON (S toggles)" : "Linear — angle snap OFF (S toggles)"} active={tool === "linear"} disabled={!hasDrawing} onClick={t("linear")} testid="tool-linear" />
      <Btn icon={Pentagon} label="Area" active={tool === "area"} disabled={!hasDrawing} onClick={t("area")} testid="tool-area" />
      <Btn icon={Crosshair} label="Count" active={tool === "count"} disabled={!hasDrawing} onClick={t("count")} testid="tool-count" />

      <div className="col-span-2 my-1 h-px w-16 bg-white/15" />

      <Btn icon={RotateCcw} label="Rotate page left 90°" disabled={!hasDrawing} onClick={() => onRotate(-1)} testid="tool-rotate-left" />
      <Btn icon={RotateCw} label="Rotate page right 90°" disabled={!hasDrawing} onClick={() => onRotate(1)} testid="tool-rotate-right" />
      <Btn icon={CheckSquare} label="Select all takeoffs on this page" disabled={!hasDrawing} onClick={onSelectAll} testid="tool-select-all" />
      <Btn icon={Copy} label="Copy selected (Ctrl/Cmd+C)" disabled={!hasSelection} onClick={onCopy} testid="tool-copy" />
      <Btn icon={ClipboardPaste} label="Paste (Ctrl/Cmd+V)" disabled={!canPaste} onClick={onPaste} testid="tool-paste" />
      <Btn icon={Undo2} label="Undo last draft point (Backspace)" disabled={!hasDraft} onClick={onUndoPoint} testid="tool-undo-point" />
      <Btn icon={History} label="Undo last change (Ctrl+Z)" disabled={!canUndo} onClick={onUndo} testid="tool-undo-change" />
      <Btn icon={Trash2} label="Delete selected takeoffs" disabled={!hasSelection} onClick={onDeleteSelected} testid="tool-delete-selected" />
    </div>
  );
}
